import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { apartments } from '@/mocks/apartments';
import type { Apartment } from '@/mocks/apartments';
import { supabase } from '@/lib/supabase';
import { mapDbApartment } from '@/lib/apartments';

function formatPrice(price: number) {
  return price.toLocaleString('fr-FR');
}

function ApartmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const [contactSent, setContactSent] = useState(false);
  const [dbApartment, setDbApartment] = useState<Apartment | null>(null);
  const [loadingDb, setLoadingDb] = useState(false);

  const mockApartment = apartments.find((a) => a.id === id);

  useEffect(() => {
    if (!id || mockApartment) return;
    let mounted = true;
    setLoadingDb(true);
    supabase
      .from('apartments')
      .select('*')
      .eq('id', id)
      .maybeSingle()
      .then(({ data }) => {
        if (!mounted) return;
        setDbApartment(data ? mapDbApartment(data) : null);
        setLoadingDb(false);
      });
    return () => {
      mounted = false;
    };
  }, [id, mockApartment]);

  const apartment = mockApartment ?? dbApartment;

  if (loadingDb) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
          <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!apartment) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-6">
          <i className="ri-home-smile-line text-3xl text-foreground-400" />
        </div>
        <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">Bien introuvable</h1>
        <p className="text-foreground-400 mb-6 text-center">Cette annonce n'existe pas ou a été retirée.</p>
        <button
          onClick={() => navigate('/apartments')}
          className="px-6 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors"
        >
          Retour aux annonces
        </button>
      </div>
    );
  }

  const related = apartments.filter((a) => a.id !== apartment.id && a.type === apartment.type).slice(0, 3);

  const handleContact = (e: React.FormEvent) => {
    e.preventDefault();
    setContactSent(true);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-400">
            <Link to="/" className="hover:text-primary-400 transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/apartments" className="hover:text-primary-400 transition-colors">Apartments</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-400 truncate">{apartment.title}</span>
          </nav>
        </div>

        {/* Gallery */}
        <section className="w-full px-4 md:px-8 lg:px-12 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 relative overflow-hidden rounded-xl h-72 md:h-[420px]">
              <img
                src={apartment.images[activeImage]}
                alt={apartment.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
              {apartment.images.slice(0, 3).map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(idx)}
                  className={`relative overflow-hidden rounded-xl h-20 md:h-[132px] border-2 transition-colors ${
                    activeImage === idx ? 'border-primary-400' : 'border-transparent'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="w-full px-4 md:px-8 lg:px-12 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        apartment.transaction === 'location'
                          ? 'bg-accent-500 text-foreground-950'
                          : 'bg-primary-500 text-foreground-950'
                      }`}>
                        {apartment.transaction === 'location' ? 'À louer' : 'À vendre'}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        apartment.available
                          ? 'bg-secondary-500/20 text-secondary-300'
                          : 'bg-foreground-800/80 text-foreground-100'
                      }`}>
                        {apartment.available ? 'Disponible' : 'Indisponible'}
                      </span>
                    </div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-2">
                      {apartment.title}
                    </h1>
                    <p className="flex items-center gap-2 text-foreground-400">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-2-line text-primary-400" />
                      </span>
                      {apartment.quartier}, {apartment.city}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-heading font-bold text-2xl text-primary-400 whitespace-nowrap">
                      {formatPrice(apartment.price)} FCFA
                    </span>
                    <span className="block text-xs text-foreground-500">
                      {apartment.transaction === 'location' ? '/ mois' : 'prix total'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Key specs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-hotel-bed-line', label: 'Chambres', value: String(apartment.bedrooms) },
                  { icon: 'ri-drop-line', label: 'Salles de bain', value: String(apartment.bathrooms) },
                  { icon: 'ri-ruler-line', label: 'Surface', value: `${apartment.surface} m²` },
                  { icon: 'ri-home-4-line', label: 'Type', value: apartment.type },
                ].map((spec) => (
                  <div key={spec.label} className="bg-background-100 border border-background-200/70 rounded-xl p-4 text-center">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-500/10 mb-2">
                      <i className={`${spec.icon} text-lg text-primary-400`} />
                    </div>
                    <span className="block font-heading font-semibold text-foreground-50">{spec.value}</span>
                    <span className="block text-xs text-foreground-400 mt-1">{spec.label}</span>
                  </div>
                ))}
              </div>

              {/* Description */}
              <div>
                <h2 className="font-heading font-bold text-xl text-foreground-50 mb-3">Description</h2>
                <p className="text-foreground-500 leading-relaxed">{apartment.description}</p>
              </div>

              {/* Amenities */}
              <div>
                <h2 className="font-heading font-bold text-xl text-foreground-50 mb-3">Équipements</h2>
                <div className="flex flex-wrap gap-2">
                  {apartment.amenities.map((amenity) => (
                    <span
                      key={amenity}
                      className="px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-400 flex items-center gap-2"
                    >
                      <i className="ri-check-line text-primary-400" />
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Owner card */}
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
                    <i className={`${apartment.ownerType === 'agence' ? 'ri-building-2-line' : 'ri-user-3-line'} text-xl text-primary-400`} />
                  </div>
                  <div>
                    <span className="block font-semibold text-foreground-50">{apartment.ownerName}</span>
                    <span className="block text-xs text-foreground-400 capitalize">
                      {apartment.ownerType === 'agence' ? 'Agence immobilière' : 'Propriétaire'}
                    </span>
                  </div>
                </div>

                <a
                  href={`tel:${apartment.ownerPhone}`}
                  className="flex items-center gap-3 px-4 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors mb-3"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line" />
                  </span>
                  {apartment.ownerPhone}
                </a>

                <p className="text-xs text-foreground-400 text-center mb-3">Annonce publiée le {apartment.postedAt}</p>

                {/* Contact form */}
                {contactSent ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-500/20 mb-3">
                      <i className="ri-check-line text-2xl text-secondary-300" />
                    </div>
                    <p className="font-semibold text-foreground-50">Message envoyé !</p>
                    <p className="text-sm text-foreground-400 mt-1">Le {apartment.ownerType} vous recontactera rapidement.</p>
                  </div>
                ) : (
                  <form onSubmit={handleContact} className="space-y-3">
                    <input
                      type="text"
                      name="name"
                      placeholder="Votre nom"
                      className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Votre téléphone"
                      className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                    />
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      placeholder="Votre message..."
                      className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400 resize-none"
                    />
                    <button
                      type="submit"
                      className="w-full px-4 py-3 border border-primary-400/50 text-primary-400 font-semibold rounded-lg hover:bg-primary-500/10 transition-colors"
                    >
                      Contacter
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-6">
                Biens similaires
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/apartments/${rel.id}`}
                    className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
                  >
                    <div className="h-40 w-full overflow-hidden">
                      <img src={rel.images[0]} alt={rel.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-sm text-foreground-50 mb-1 line-clamp-1">{rel.title}</h3>
                      <p className="text-xs text-foreground-400 mb-2">{rel.quartier}, {rel.city}</p>
                      <span className="font-heading font-bold text-primary-400">{formatPrice(rel.price)} FCFA</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ApartmentDetailPage;